/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';

import { toast } from 'react-toastify';

import { useDispatch } from 'react-redux';

import { cloneDeep } from 'lodash';
import JSZip from 'jszip';
import saveAs from 'file-saver';
import { CartReducer } from '../../../Redux/Reducers/CartReducer';
import { useAppSelector } from '../../../Redux/Store/hooks';
import { CartItem, CartItemHelper } from '../../Data/CartItem';

import { PickedFilesHandler } from '../../Util/PickedFilesHandler';
import { OrderView } from '../Views/OrderView';
import { POSOrderManager } from '../../Services/POSOrderManager';
import { POSCatalogData, POSCatalogModelData } from '../../../common/libraries/modelViewer/Data/POSCatalogData';
import { CustomAttributesReducer } from '../../../Redux/Reducers/CustomAttributesReducer';
import { TextInputComponent } from '../../../common/libraries/react/inputs/text/textInput';
import { RequestOrderVerificationCode, ResponseOrderVerificationCode } from '../../../common/libraries/requests/orders/requestOrderVerificationCode';
import { POS_BACKEND_PORT } from '../../../globals/constants';
import { OrderSessionData } from '../../../common/libraries/fileFormats/catalogOrder/catalogOrder';

type OrderControllerParameters = {
  catalog : POSCatalogData,
  refPickedFilesHandler : React.MutableRefObject<PickedFilesHandler>
  orderSessionData: OrderSessionData,
};

export function OrderController(
  {
    catalog,
    refPickedFilesHandler,
    orderSessionData,
  } : OrderControllerParameters,
) {

  // Grab the dispatch hook from Redux
  const dispatch = useDispatch();

  const [validationCodeInput, setValidationCodeInput] = useState<string>('');
  const [validationCode, setValidationCode] = useState<string>('none');
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [showDemoConfirmDialog, setShowDemoConfirmDialog] = useState<boolean>(false);
  const [showCodeConfirmDialog, setShowCodeConfirmDialog] = useState<boolean>(false);
  const [orderSubmitionSuccess, setOrderSubmitionSuccess] = useState<boolean>(false);
  const [orderSubmitionOverlay, setOrderSubmitionOverlay] = useState<boolean>(false);
  const [codeValidationError, setcodeValidationError] = useState<boolean>(false);
  const [globalShowPrices, setGlobalShowPrices] = useState<boolean>(false);
  const [globalOrderPreviewFontSize, setGlobalOrderPreviewFontSize] = useState<number>(7);
  const [globalOrderPreviewImageWidth, setGlobalOrderPreviewImageWidth] = useState<number>(250);
  const [globalOrderEndCustNameAttrName, setGlobalOrderEndCustNameAttrName] = useState<string>('endCustomerName');

  useEffect(() => {
    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);

    const showPricesValue = computedStyle.getPropertyValue('--show-prices').trim();

    setGlobalShowPrices(showPricesValue === 'true');

    const orderEndCustNameAttrName = computedStyle.getPropertyValue('--order-end-customer-name-attr').trim();
    if (orderEndCustNameAttrName && orderEndCustNameAttrName.length > 0) {
      setGlobalOrderEndCustNameAttrName(orderEndCustNameAttrName);
    }

    const orderPreviewFontSize = computedStyle.getPropertyValue('--order-preview-fontsize').trim();
    const orderPreviewImageWidth = computedStyle.getPropertyValue('--order-preview-image-width').trim();

    const fontSize = Number.parseFloat(orderPreviewFontSize);
    const imageWidth = Number.parseFloat(orderPreviewImageWidth);

    if (Number.isFinite(fontSize) && fontSize > 0) {
      setGlobalOrderPreviewFontSize(fontSize);
    }

    if (Number.isFinite(imageWidth) && imageWidth > 0) {
      setGlobalOrderPreviewImageWidth(imageWidth);
    }
  }, []);

  // From the store, get the cart
  const sessionToken = useAppSelector((store) => {
    return store.ProfileReducer.sessionToken;
  });

  // From the store, get the cart
  const cartItems = useAppSelector((store) => {
    return store.CartReducer.cartItems;
  });

  // From the store, get the strings
  const strings = useAppSelector((state) => {
    return state.TextsReducer.strings;
  })!;

  const translations = useAppSelector((state) => {
    return state.TextsReducer.translation;
  })!;

  const lang = useAppSelector((state) => {
    return state.TextsReducer.language;
  });

  // From the store, get the environment settings
  const environmentSettings = useAppSelector((state) => {
    return state.EnvironmentSettingsReducer;
  });

  // From the store, get the custom attributes
  const customAttributesValues = useAppSelector((state) => {
    return state.CustomAttributesReducer.customAttributesValues;
  })!;

  // Grab the actions from the Cart Reducer
  const { actions: cartActions } = CartReducer;

  // Grab the actions from the Custom Attributes Reducer
  const { actions: customAttributesActions } = CustomAttributesReducer;

  // Since we're going to change the object passed, create a copy of it
  const localCartItems = cloneDeep(cartItems);

  // Sort the items so related items are presented near each other
  localCartItems.sort((cartItemA, cartItemB) => {
    // First, sort by collection name

    const itemACollectionName = CartItemHelper.GetCollectionName(cartItemA);
    const itemBCollectionName = CartItemHelper.GetCollectionName(cartItemB);

    const cmpCollectionName = itemACollectionName.localeCompare(itemBCollectionName);

    if (cmpCollectionName !== 0) {
      return cmpCollectionName;
    }

    // Then by Model Name
    const itemAObjectName = CartItemHelper.GetObjectName(cartItemA);
    const itemBObjectName = CartItemHelper.GetObjectName(cartItemB);

    const cmpObjectName = itemAObjectName.localeCompare(itemBObjectName);

    if (cmpObjectName !== 0) {
      return cmpObjectName;
    }

    // Then by variant. Put the customized ones towards the end
    // To get the variant name, we need to get the original objects

    const catalogItemA = catalog.getModelByCollectionNameAndModelName(
      CartItemHelper.GetCollectionName(cartItemA),
      CartItemHelper.GetObjectName(cartItemA),
    );

    const catalogItemB = catalog.getModelByCollectionNameAndModelName(
      CartItemHelper.GetCollectionName(cartItemB),
      CartItemHelper.GetObjectName(cartItemB),
    );

    if (catalogItemA === undefined
      || catalogItemB === undefined) {
      return 0;
    }

    const variantDescriptionA = POSCatalogModelData.GetVariantDescription(
      CartItemHelper.GetVariant(cartItemA),
      catalogItemA.getVariants(),
    );
    const variantDescriptionB = POSCatalogModelData.GetVariantDescription(
      CartItemHelper.GetVariant(cartItemB),
      catalogItemB.getVariants(),
    );

    if (variantDescriptionA === '') {
      return 1;
    }
    if (variantDescriptionB === '') {
      return -1;
    }

    return variantDescriptionA.localeCompare(variantDescriptionB);
  });

  // Assign an unique ID to each cart item
  localCartItems.forEach((cartItem, index) => {
    CartItemHelper.SetCartIndex(cartItem, index);
  });

  // Callback to handle new quantity
  const handleChangeQuantity = (cartItem : CartItem, newQuantity : number) => {
    if (newQuantity <= 0) {
      // Do nothing
    } else {
      dispatch(
        cartActions.changeQuantity({
          cartItem,
          quantity: newQuantity,
        }),
      );
    }
  };

  // Callback to handle removing an item
  const handleRemoveItem = (cartItem : CartItem) => {
    dispatch(cartActions.removeFromCart({ cartItem }));
  };

  // Callback to handle clearing the cart
  const handleClearCart = () => {
    dispatch(cartActions.clearCart());
    dispatch(customAttributesActions.clearAttributes());
  };

  const GetContactEmailToUse = () : string | undefined => {
    if (orderSessionData.customerEmailAccount) {
      return orderSessionData.customerEmailAccount;
    }

    const contactEmailAttribute = customAttributesValues.find((att) => att.attributeKey.toLowerCase() === 'contactemail');

    if (contactEmailAttribute) {
      return contactEmailAttribute.attributeValue;
    }
    return undefined;
  };

  const handleOrderPreview = async () => {

    const bDoSaveInZip = false;
    const uniqueFileName = strings.PREVIEW_ORDER_BUTTON;

    let blob : Blob | undefined;

    // Generate the PDF (w/o packing ZIP)
    blob = await POSOrderManager.generateOrderPDFDocument(
      uniqueFileName,
      cartItems,
      customAttributesValues,
      catalog,
      strings,
      environmentSettings,
      orderSessionData,
      lang,
      globalShowPrices,
      globalOrderPreviewFontSize,
      globalOrderPreviewImageWidth,
      globalOrderEndCustNameAttrName,
    );

    if (bDoSaveInZip && blob) {
      const zip = new JSZip();
      zip.file(
        `${uniqueFileName}.pdf`,
        blob,
        { base64: true },
      );
      // Reset blob as the wrapping zip
      blob = await zip.generateAsync({ type: 'blob' });
    }
    // Download the file in the browser...
    if (blob) saveAs(blob, `${uniqueFileName}.${bDoSaveInZip ? 'zip' : 'pdf'}`);
  };

  const handleStartOrderSubmission = async () => {

    if (!catalog || !catalog.catalogFileInfo) return;

    if (GetContactEmailToUse() === undefined) {

      setShowDemoConfirmDialog(true);
      setShowConfirmDialog(false);
      setOrderSubmitionSuccess(true);
      handleClearCart();
      return;
    }
    setShowDemoConfirmDialog(false);

    setShowCodeConfirmDialog(true);
    const request = new RequestOrderVerificationCode();
    request.language = lang;
    const orderData = await POSOrderManager.generateOrderData(
      environmentSettings,
      cartItems,
      customAttributesValues,
      catalog,
      orderSessionData,
      lang,
    );
    // Check if the order data was generated
    if (orderData === undefined) {
      return;
    }
    request.orderData = orderData;
    request.sessionToken = sessionToken;

    const urlComponents = window.location.href.split(':', 2);
    // TODO: Update Url to use the url that comes on the catalog
    console.log(catalog.catalogFileInfo.sourceData.channelBackendInsertOrderEndpoint);
    const url = catalog.catalogFileInfo.sourceData.channelBackendInsertOrderEndpoint || `${urlComponents[0]}:${urlComponents[1]}:${POS_BACKEND_PORT}`;
    const endpoint = new URL(request.getAPIEndPoint(), url).toString();

    // Convert the contents of the request to string
    const bodyContents = JSON.stringify(request);

    // Configure and perform the request
    const response = await fetch(
      endpoint,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: bodyContents,
      },
    );
    const body = await response.text();

    const responseObj = JSON.parse(body) as ResponseOrderVerificationCode;
    if (responseObj.result) {
      setValidationCode(responseObj.verificationCode);
    }
  };

  // Callback to handle the document generation
  const handleCreateOrderDocument = async () => {

    if (validationCode !== validationCodeInput) {
      setcodeValidationError(true);
      return;
    }

    setShowCodeConfirmDialog(false);
    setOrderSubmitionOverlay(true);
    const result = await POSOrderManager.handleOrderCreation(
      sessionToken,
      cartItems,
      customAttributesValues,
      catalog,
      refPickedFilesHandler.current,
      strings,
      lang,
      environmentSettings,
      translations,
      orderSessionData,
    );

    setShowConfirmDialog(true);
    setOrderSubmitionSuccess(result);
    if (result) {
      handleClearCart();
    }
  };

  const submitionPopUp = (title: string, info: string, result: boolean) : JSX.Element => {

    return (
      <div className="order-confirmation-dialog">
        <div className="d-flex justify-content-center title">
          {title}
        </div>
        <div className="d-flex justify-content-center info">
          {info}
        </div>
        <div className="d-flex justify-content-around actions">
          {result
            ? (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setOrderSubmitionOverlay(false);
                  setShowConfirmDialog(false);
                  window.location.reload();
                }}
              >
                {strings.OK_ACTION}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => {
                    setOrderSubmitionOverlay(false);
                    setShowConfirmDialog(false);
                  }}
                >
                  {strings.CANCEL_ACTION}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowConfirmDialog(false);
                    handleCreateOrderDocument();
                  }}
                >
                  {strings.RETRY_ACTION}
                </button>
              </>
            )}
        </div>
      </div>
    );

    // After the order is created, clear the cart
  };

  const submitionOverlay = () : JSX.Element => {
    return (
      <div className="order-code-overlay" />
    );

    // After the order is created, clear the cart
  };

  const codeSubmitionPopUp = () : JSX.Element => {
    return (
      <div className="order-code-overlay">
        <div className="order-code-dialog">
          <div className="d-flex justify-content-center title">
            {strings.VALIDATION_POP_UP_TITLE}
          </div>
          <div className="d-flex justify-content-center info">
            {strings.VALIDATION_POP_UP_MESSAGE}
          </div>
          { codeValidationError ? <p className="justify-content-center error">{strings.VALIDATION_POP_UP_ERROR}</p> : <p /> }
          <TextInputComponent id="CodeInsert" value={validationCodeInput} onSaveValue={(value) => { setValidationCodeInput(value); }} />
          <div className="d-flex justify-content-around actions">
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => {
                setValidationCodeInput('');
                setcodeValidationError(false);
                setShowCodeConfirmDialog(false);
              }}
            >
              {strings.CANCEL_ACTION}
            </button>
            <button
              type="button"
              className="btn btn-outline-dark"
              onClick={() => {
                handleStartOrderSubmission();
              }}
            >
              {strings.RESEND_ACTION}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                handleCreateOrderDocument();
              }}
            >
              {strings.SUBMIT_LABEL}
            </button>
          </div>
        </div>
      </div>
    );

    // After the order is created, clear the cart
  };

  return (
    <>
      {(
        catalog !== undefined
        && localCartItems !== undefined
        && localCartItems.length > 0
        && catalog.catalogFileInfo !== undefined
        && catalog.catalogFileInfo?.itemsCustomAttributesDefinitions !== undefined
      ) && (
        <OrderView
          catalog={catalog}
          cartItems={localCartItems}
          changeQuantityHandler={handleChangeQuantity}
          removeItemHandler={handleRemoveItem}
          clearCartHandler={handleClearCart}
          createOrderDocumentHandler={handleStartOrderSubmission}
          downloadOrderPreview={handleOrderPreview}
          customOrderLineAttributesData={catalog.catalogFileInfo?.itemsCustomAttributesDefinitions}
          lang={lang}
        />
      )}
      {orderSubmitionOverlay && submitionOverlay()}
      {showCodeConfirmDialog && codeSubmitionPopUp()}
      {showConfirmDialog && (orderSubmitionSuccess
        ? submitionPopUp(strings.SUCCESS_LABEL, strings.ORDER_SUCCESS_MESSAGE, true)
        : submitionPopUp(strings.ERROR_LABEL, strings.ORDER_ERROR_MESSAGE, false))}
      {showDemoConfirmDialog && (orderSubmitionSuccess
        ? submitionPopUp(strings.SUCCESS_LABEL, strings.DEMO_ORDER_SUCCESS_MESSAGE, true)
        : submitionPopUp(strings.ERROR_LABEL, strings.DEMO_ORDER_ERROR_MESSAGE, false))}

    </>
  );
}
